import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { fleetService } from '../services/fleetService';
import AddDriverModal from '../components/AddDriverModal';
import { ArrowLeft, User, Mail, Phone, DollarSign, TrendingUp, Star, Car, Hash, FileText, Eye, Pencil, X } from 'lucide-react';
import { useToast } from '../contexts/ToastContext';
import { cn } from '../utils/cn';

const documentStatusConfig = {
  pending: { label: 'Pending', color: 'warning' },
  approved: { label: 'Approved', color: 'success' },
  rejected: { label: 'Rejected', color: 'danger' },
};

export default function DriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [pickVehicleId, setPickVehicleId] = useState('');

  const { data: driverData, isLoading } = useQuery({
    queryKey: ['fleet-driver', id],
    queryFn: () =>
      fleetService.getDriver(id).then((res) => {
        const body = res.data ?? res;
        return body?.data ?? body;
      }),
  });

  const { data: walletData } = useQuery({
    queryKey: ['fleet-driver-wallet', id],
    queryFn: () => fleetService.getDriverWallet(id).then((res) => res.data),
    enabled: !!id,
  });

  const canManageVehicle =
    !!id &&
    !isLoading &&
    driverData?.approval_status === 'approved' &&
    driverData?.status === 'active';

  const { data: vehiclesListRes } = useQuery({
    queryKey: ['fleet-vehicles-for-driver-assign', id],
    queryFn: () =>
      fleetService.getVehicles({ status: 'approved', limit: 200, page: 1 }).then((res) => res.data),
    enabled: !!id && canManageVehicle && assignModalOpen,
  });

  const assignVehicleMutation = useMutation({
    mutationFn: (vehicleId) => fleetService.assignVehicleToDriver(vehicleId, id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-driver', id] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicle'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers-for-assign'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles-for-driver-assign', id] });
      showSuccess('Vehicle assigned.');
      setAssignModalOpen(false);
      setPickVehicleId('');
    },
    onError: (err) =>
      showError(err?.response?.data?.message || err.message || 'Could not assign vehicle'),
  });

  const unassignVehicleMutation = useMutation({
    mutationFn: (vehicleId) => fleetService.unassignVehicle(vehicleId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['fleet-driver', id] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-vehicle'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers'] });
      queryClient.invalidateQueries({ queryKey: ['fleet-drivers-for-assign'] });
      showSuccess('Vehicle unassigned.');
    },
    onError: (err) =>
      showError(err?.response?.data?.message || err.message || 'Could not unassign'),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-text-secondary dark:text-text-secondary-dark">Loading driver details...</div>
        </div>
      </div>
    );
  }

  const driver = driverData || {};
  const vehicle = driver.vehicle || null;
  const documents = Array.isArray(driver.documents) ? driver.documents : [];
  const availableVehicles = (vehiclesListRes?.data ?? []).filter(
    (v) => !v.driver_id || v.driver_id === id
  );
  const initials = `${driver.first_name?.charAt(0) || ''}${driver.last_name?.charAt(0) || ''}`.toUpperCase() || 'D';
  const fullName = `${driver.first_name || ''} ${driver.last_name || ''}`.trim() || 'Driver';

  return (
    <div className="space-y-4 lg:space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          onClick={() => navigate('/drivers')}
          className="flex items-center gap-2 text-text-secondary dark:text-text-secondary-dark hover:text-text dark:hover:text-text-dark transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Drivers</span>
        </button>
        <button
          type="button"
          onClick={() => setEditModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium shadow-md"
        >
          <Pencil className="w-4 h-4" />
          Edit driver
        </button>
      </div>

      <AddDriverModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          queryClient.invalidateQueries(['fleet-driver', id]);
        }}
        mode="edit"
        driverId={id}
      />

      {/* Profile Header */}
      <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary-dark/10 dark:to-primary-dark/5 rounded-2xl p-6 lg:p-8 border border-primary/20 dark:border-primary-dark/20 shadow-lg">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="w-20 h-20 lg:w-24 lg:h-24 bg-gradient-to-br from-primary to-primary-dark rounded-2xl flex items-center justify-center text-white font-bold text-2xl lg:text-3xl shadow-xl">
            {initials}
          </div>
          <div className="flex-1">
            <h1 className="text-2xl lg:text-3xl font-bold text-text dark:text-text-dark mb-2">
              {fullName}
            </h1>
            <p className="text-text-secondary dark:text-text-secondary-dark mb-4">{driver.email}</p>
            <div className="flex flex-wrap gap-4">
              {driver.driver_rating && (
                <div className="flex items-center gap-2">
                  <Star className="w-5 h-5 text-warning fill-warning" />
                  <span className="font-semibold text-text dark:text-text-dark">
                    {driver.driver_rating.toFixed(1)}
                  </span>
                </div>
              )}
              {driver.total_rides && (
                <div className="flex items-center gap-2 text-text-secondary dark:text-text-secondary-dark">
                  <TrendingUp className="w-4 h-4" />
                  <span>{driver.total_rides} rides</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-success/10 to-success/5 dark:from-success-dark/10 dark:to-success-dark/5 rounded-xl p-6 border border-success/20 dark:border-success-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Total Rides</p>
            <TrendingUp className="w-5 h-5 text-success" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            {driver.total_rides || 0}
          </p>
        </div>
        <div className="bg-gradient-to-br from-warning/10 to-warning/5 dark:from-warning-dark/10 dark:to-warning-dark/5 rounded-xl p-6 border border-warning/20 dark:border-warning-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Rating</p>
            <Star className="w-5 h-5 text-warning fill-warning" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            {driver.driver_rating ? driver.driver_rating.toFixed(1) : 'N/A'}
          </p>
        </div>
        <div className="bg-gradient-to-br from-info/10 to-info/5 dark:from-info-dark/10 dark:to-info-dark/5 rounded-xl p-6 border border-info/20 dark:border-info-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Total Earnings</p>
            <DollarSign className="w-5 h-5 text-info" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            NOK {(driver.totalEarnings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
        <div className="bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary-dark/10 dark:to-primary-dark/5 rounded-xl p-6 border border-primary/20 dark:border-primary-dark/20 shadow-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-sm font-semibold text-text-secondary dark:text-text-secondary-dark">Wallet Balance</p>
            <DollarSign className="w-5 h-5 text-primary" />
          </div>
          <p className="text-3xl font-bold text-text dark:text-text-dark">
            NOK {(walletData?.wallet?.balance || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        </div>
      </div>

      {/* Information Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
        {/* Driver Information */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <User className="w-5 h-5 text-primary" />
            Driver Information
          </h2>
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <Mail className="w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">Email</p>
                <p className="text-sm font-medium text-text dark:text-text-dark">{driver.email}</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
              <Phone className="w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">Phone</p>
                <p className="text-sm font-medium text-text dark:text-text-dark">{driver.phone || 'N/A'}</p>
              </div>
            </div>
            {driver.driver_license_number && (
              <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                <User className="w-5 h-5 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">License Number</p>
                  <p className="text-sm font-medium text-text dark:text-text-dark">{driver.driver_license_number}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Vehicle Information */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <Car className="w-5 h-5 text-primary" />
            Assigned Vehicle
          </h2>
          {canManageVehicle && (
            <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-4">
              Assign from here or let the driver choose an available approved vehicle in the app. Only one vehicle
              is active per driver.
            </p>
          )}
          {!canManageVehicle && driver.approval_status === 'approved' && driver.status !== 'active' && (
            <p className="text-sm text-warning mb-4">Inactive drivers cannot be assigned a vehicle.</p>
          )}
          {driver.approval_status !== 'approved' && (
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
              Approve this driver before assigning a vehicle.
            </p>
          )}
          {vehicle ? (
            <div className="space-y-4">
              <div className="bg-gradient-to-r from-info/10 to-info/5 dark:from-info-dark/10 dark:to-info-dark/5 rounded-xl p-4 border border-info/20 dark:border-info-dark/20">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 bg-gradient-to-br from-info to-info-dark rounded-xl flex items-center justify-center shadow-lg">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-text dark:text-text-dark">
                      {vehicle.make} {vehicle.model}
                    </h3>
                    <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
                      {vehicle.year} • {vehicle.color}
                    </p>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                  <Hash className="w-4 h-4 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">License Plate</p>
                    <p className="text-sm font-medium font-mono text-text dark:text-text-dark">
                      {vehicle.license_plate || vehicle.licensePlate}
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                  <Car className="w-4 h-4 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">Type</p>
                    <p className="text-sm font-medium text-text dark:text-text-dark capitalize">
                      {vehicle.vehicle_type || vehicle.vehicleType || 'Standard'}
                    </p>
                  </div>
                </div>
              </div>
              {vehicle.capacity && (
                <div className="flex items-start gap-3 p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                  <User className="w-4 h-4 text-text-tertiary dark:text-text-tertiary-dark mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">Capacity</p>
                    <p className="text-sm font-medium text-text dark:text-text-dark">
                      {vehicle.capacity} seats
                    </p>
                  </div>
                </div>
              )}
              {vehicle.id && (
                <Link
                  to={`/vehicles/${vehicle.id}`}
                  className="inline-flex text-sm text-primary font-medium hover:underline"
                >
                  Open vehicle details
                </Link>
              )}
              {canManageVehicle && (
                <div className="flex flex-wrap gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => {
                      setPickVehicleId('');
                      setAssignModalOpen(true);
                    }}
                    className="px-4 py-2 border border-border dark:border-border-dark rounded-lg text-sm font-medium hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark"
                  >
                    Change vehicle
                  </button>
                  <button
                    type="button"
                    disabled={unassignVehicleMutation.isPending}
                    onClick={() => {
                      if (window.confirm('Remove this vehicle assignment?')) {
                        unassignVehicleMutation.mutate(vehicle.id);
                      }
                    }}
                    className="px-4 py-2 text-sm font-medium text-danger rounded-lg border border-danger/30 hover:bg-danger-light dark:hover:bg-danger-dark/20 disabled:opacity-50"
                  >
                    {unassignVehicleMutation.isPending ? 'Removing…' : 'Remove assignment'}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <div className="w-16 h-16 rounded-full bg-warning-light dark:bg-warning-dark flex items-center justify-center mb-4">
                <Car className="w-8 h-8 text-warning opacity-50" />
              </div>
              <h3 className="text-base font-semibold text-text dark:text-text-dark mb-2">
                No Vehicle Assigned
              </h3>
              <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
                This driver does not have an approved vehicle linked yet.
              </p>
              {canManageVehicle ? (
                <button
                  type="button"
                  onClick={() => {
                    setPickVehicleId('');
                    setAssignModalOpen(true);
                  }}
                  className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors text-sm font-medium"
                >
                  Assign vehicle
                </button>
              ) : null}
            </div>
          )}
        </div>

        {/* Documents (uploaded when driver was added or via driver app) */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg lg:col-span-2">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Attached documents
          </h2>
          {documents.length === 0 ? (
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark">
              No documents on file for this driver yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {documents.map((doc) => {
                const title = doc.display_name || doc.displayName || doc.document_type || doc.type || 'Document';
                const fileLabel = doc.file_name || doc.fileName || '';
                const statusKey = doc.status || 'pending';
                const docStatus = documentStatusConfig[statusKey] || documentStatusConfig.pending;
                const viewUrl = doc.url;
                return (
                  <div
                    key={doc.id}
                    className="rounded-xl p-4 border border-border dark:border-border-dark bg-surface-secondary dark:bg-surface-secondary-dark"
                  >
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold text-text dark:text-text-dark truncate">{title}</p>
                        {fileLabel ? (
                          <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark mt-1 truncate">
                            {fileLabel}
                          </p>
                        ) : null}
                      </div>
                      <span
                        className={cn(
                          'shrink-0 px-2 py-1 rounded-full text-xs font-medium',
                          docStatus.color === 'success'
                            ? 'bg-success-light dark:bg-success-dark text-success'
                            : docStatus.color === 'warning'
                              ? 'bg-warning-light dark:bg-warning-dark text-warning'
                              : 'bg-danger-light dark:bg-danger-dark text-danger'
                        )}
                      >
                        {docStatus.label}
                      </span>
                    </div>
                    {viewUrl ? (
                      <a
                        href={viewUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-primary hover:text-primary-dark font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        View document
                      </a>
                    ) : (
                      <p className="text-xs text-text-tertiary dark:text-text-tertiary-dark">Preview link unavailable</p>
                    )}
                    {(doc.rejection_reason || doc.rejectionReason) && (
                      <p className="text-xs text-danger mt-2">
                        Note: {doc.rejection_reason || doc.rejectionReason}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Additional Stats */}
        <div className="bg-surface dark:bg-surface-dark rounded-2xl p-6 border border-border dark:border-border-dark shadow-lg">
          <h2 className="text-xl font-bold text-text dark:text-text-dark mb-6 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Performance Metrics
          </h2>
          <div className="space-y-4">
            {walletData?.earnings && (
              <>
                <div className="p-4 rounded-lg bg-gradient-to-r from-success/10 to-success/5 dark:from-success-dark/10 dark:to-success-dark/5 border border-success/20">
                  <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">Total Earnings</p>
                  <p className="text-2xl font-bold text-text dark:text-text-dark">
                    NOK {(walletData.earnings.total || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                    <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">This Week</p>
                    <p className="text-lg font-bold text-text dark:text-text-dark">
                      NOK {(walletData.earnings.thisWeek || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-3 rounded-lg bg-surface-secondary dark:bg-surface-secondary-dark">
                    <p className="text-xs text-text-secondary dark:text-text-secondary-dark mb-1">This Month</p>
                    <p className="text-lg font-bold text-text dark:text-text-dark">
                      NOK {(walletData.earnings.thisMonth || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {assignModalOpen && canManageVehicle && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="assign-vehicle-title"
        >
          <div className="bg-surface dark:bg-surface-dark rounded-2xl border border-border dark:border-border-dark shadow-xl max-w-md w-full p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <h2 id="assign-vehicle-title" className="text-lg font-bold text-text dark:text-text-dark">
                Assign vehicle
              </h2>
              <button
                type="button"
                onClick={() => {
                  setAssignModalOpen(false);
                  setPickVehicleId('');
                }}
                className="p-1 rounded-lg hover:bg-surface-secondary dark:hover:bg-surface-secondary-dark text-text-secondary"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-text-secondary dark:text-text-secondary-dark mb-4">
              Choose an approved vehicle that is free or already linked to this driver. Assigning a new vehicle
              replaces the previous one.
            </p>
            <select
              value={pickVehicleId}
              onChange={(e) => setPickVehicleId(e.target.value)}
              className="w-full px-3 py-2 rounded-lg border border-border dark:border-border-dark bg-surface dark:bg-surface-dark text-text dark:text-text-dark mb-4"
            >
              <option value="">Select vehicle…</option>
              {availableVehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.make} {v.model} — {v.license_plate || v.licensePlate}
                  {v.driver_id === id ? ' (current)' : ''}
                </option>
              ))}
            </select>
            {availableVehicles.length === 0 && (
              <p className="text-sm text-warning mb-4">
                No eligible vehicles. Add an approved vehicle or unassign one from another driver first.
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setAssignModalOpen(false);
                  setPickVehicleId('');
                }}
                className="px-4 py-2 rounded-lg border border-border dark:border-border-dark text-sm font-medium"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!pickVehicleId || assignVehicleMutation.isPending}
                onClick={() => pickVehicleId && assignVehicleMutation.mutate(pickVehicleId)}
                className="px-4 py-2 rounded-lg bg-primary text-white text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
              >
                {assignVehicleMutation.isPending ? 'Saving…' : 'Assign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
